import React, { useMemo } from 'react'

import { Stack, Typography } from '@mui/material'

import {
  ActionLine,
  CompanyAvatar,
  statusFilterOptions,
  SUPPORT_TYPE_VALUES,
  SupportTypeBadge,
} from 'components'
import {
  SUPPORT_REPORT_SOURCES,
  SUPPORT_REQUEST_STATUSES_INTERNAL,
  SUPPORT_REQUEST_TYPES,
} from 'constants/statuses'
import { errorRefsOf, lastActivityAt, supportReportSource } from 'utils/supportReportSource'

import type { SupportRequest } from 'services/supportApi'

/**
 * Search / Filter / Sort for the internal queue — the same toolbar the brand list uses.
 *
 * ## Why this is a sibling of `SupportListControls` rather than that component
 *
 * The brand toolbar is coupled to the brand's status VOCABULARY in a way that is not a
 * prop: it filters and sorts on `displayStatusKey`, which swaps a promoted feature's
 * request status for its BOARD status so the customer's list and detail agree. Internal
 * must not do that — a staffer reads `open / in_progress / waiting_on_customer` precisely
 * because those are the states they act on, and `RequestThread` deliberately renders the
 * internal side without `preferBoardStatus` for the same reason. Importing the brand
 * module and passing a flag would put that decision in two places at once.
 *
 * What IS shared is everything that matters: the `ActionLine` toolbar itself, the status
 * pill options, and `SupportTypeBadge` as the type facet's own renderer (§28/§36) — so the
 * facet a staffer selects is the same token the card shows, not a lookalike.
 *
 * Internal also has a facet the brand list cannot have: **company**. The brand list is
 * one company by definition; this queue is every company, which makes "show me only
 * Louis Vuitton" the single most useful narrowing on the page and the reason a toolbar
 * was worth adding here at all.
 *
 * Client-side, for the same reason the brand list is: the endpoint returns the whole
 * queue, which is tens of rows and already in memory. If it ever grows past that, this
 * function's signature is the shape to move server-side, unchanged.
 */

/** One entry of the shared drawer's applied-filter model. */
export interface InternalFilterItem {
  field: string
  operator: string
  value: string[]
}

/** One entry of the shared `Sort` control's model — the same shape a DataGrid sort model uses. */
export interface InternalSortItem {
  field: string
  sort: 'asc' | 'desc'
}

export interface InternalListFilters {
  query: string
  filters: InternalFilterItem[]
  /** Applied orderings. Empty = the default below. */
  sort: InternalSortItem[]
}

export const EMPTY_INTERNAL_FILTERS: InternalListFilters = {
  query: '',
  filters: [],
  sort: [],
}

/**
 * Newest ACTIVITY first, applied whenever the user has not chosen an ordering.
 *
 * VRG-726: was newest-CREATED first. With automatic reports in the queue that buried the row
 * that matters: an automatic report filed last week that recurred ten minutes ago, or that a
 * person has just written in about, is today's problem. `lastActivityAt` is the newest of
 * created, updated (replies, status changes, a linked note) and last seen (a recurrence).
 *
 * Held here rather than seeded into `sort` so the Sort button reads as un-applied until
 * the user actually deviates — a permanently-lit count badge carries no information.
 */
export const DEFAULT_SORT: InternalSortItem[] = [{ field: 'lastActivityAt', sort: 'desc' }]

/** True when the user has NARROWED the queue — decides "no matches" vs "nothing in the queue". */
export const hasActiveInternalFilters = (f: InternalListFilters): boolean =>
  f.query.trim().length > 0 || f.filters.length > 0

/**
 * The company facet's field.
 *
 * `companyName` is nullable on the wire, and a null must still be selectable — a request
 * whose company row was deleted is exactly the kind of thing a staffer goes looking for.
 * So the facet reads through this rather than off the raw column, and the label it
 * produces is the one the card already shows.
 */
export const UNKNOWN_COMPANY = 'Unknown company'
const companyLabel = (request: SupportRequest): string => request.companyName ?? UNKNOWN_COMPANY

/**
 * Read one filterable/sortable field as the row DISPLAYS it.
 *
 * `reportSource` and `lastActivityAt` are DERIVED (see `utils/supportReportSource`), not wire
 * fields. `reportSource` is deliberately not called `source`: the wire's `source` is the
 * SURFACE a request was filed from (platform, scanner app), a different question.
 */
const readField = (request: SupportRequest, field: string): string => {
  if (field === 'companyName') return companyLabel(request)
  if (field === 'reportSource') return supportReportSource(request)
  if (field === 'lastActivityAt') return lastActivityAt(request)
  return String((request as unknown as Record<string, unknown>)[field] ?? '')
}

/**
 * Compare two requests on one field, by what the field MEANS.
 *
 * Dates compare as instants, not as the strings they arrive in. Everything else falls back
 * to its display LABEL — which is what makes the shared control's "A - Z" honest: it orders
 * by the words on screen ("Bug", "Feature", "Waiting on customer"), not by the raw enum
 * value behind them.
 */
const compareOn = (a: SupportRequest, b: SupportRequest, field: string): number => {
  if (field === 'createdAt' || field === 'updatedAt' || field === 'lastActivityAt') {
    return new Date(readField(a, field)).getTime() - new Date(readField(b, field)).getTime()
  }
  if (field === 'occurrenceCount') return (a.occurrenceCount ?? 1) - (b.occurrenceCount ?? 1)

  // Widened to a plain lookup deliberately: a union of two STATUSES maps would make
  // `keyof typeof labels` their INTERSECTION, so indexing it resolves to `never`.
  const labels: Record<string, { label: string }> | undefined =
    field === 'type'
      ? SUPPORT_REQUEST_TYPES
      : field === 'status'
        ? SUPPORT_REQUEST_STATUSES_INTERNAL
        : field === 'reportSource'
          ? SUPPORT_REPORT_SOURCES
          : undefined

  const read = (r: SupportRequest) => {
    const key = readField(r, field)
    return labels?.[key]?.label ?? key
  }

  return read(a).localeCompare(read(b))
}

/** Apply search, facets and ordering to the queue, client-side. */
export const applyInternalFilters = (
  requests: SupportRequest[],
  f: InternalListFilters,
): SupportRequest[] => {
  const q = f.query.trim().toLowerCase()

  const filtered = requests.filter(request => {
    // Every facet must match (AND across fields), any value within one may (OR within a
    // field) — the standard faceted-browse reading, and the one the rest of the app uses.
    const matchesFacets = f.filters.every(filter => {
      if (!Array.isArray(filter.value) || filter.value.length === 0) return true
      return filter.value.includes(readField(request, filter.field))
    })
    if (!matchesFacets) return false
    if (!q) return true

    // The five things a staffer has to hand when hunting for a request. Company is in
    // here and not in the brand's version, because on a cross-company queue "the Louis
    // Vuitton one" is how the request gets described to you. The reference is matched
    // with and without its "#", because that is how people type it.
    // VRG-726: plus the error refs, so a ref pasted from a log line or a customer's
    // screenshot finds its report.
    const refs = errorRefsOf(request)
    const haystack = [
      request.body,
      `#${request.reference}`,
      String(request.reference),
      request.submitterName ?? '',
      request.submitterEmail ?? '',
      companyLabel(request),
      refs.first ?? '',
      refs.latest ?? '',
    ]
      .join(' ')
      .toLowerCase()

    return haystack.includes(q)
  })

  const model = f.sort.length > 0 ? f.sort : DEFAULT_SORT

  // Sorted on a COPY — `filter` already returned a new array, but returning a sorted view
  // of the query cache's own array would mutate cached data in place.
  return [...filtered].sort((a, b) => {
    for (const entry of model) {
      const comparison = compareOn(a, b, entry.field)
      if (comparison !== 0) return entry.sort === 'desc' ? -comparison : comparison
    }
    return 0
  })
}

interface Props {
  value: InternalListFilters
  onChange: (next: InternalListFilters) => void
  /** The loaded queue. Only used to derive the company facet's options. */
  requests: SupportRequest[]
}

export const InternalSupportListControls = ({ value, onChange, requests }: Props) => {
  const set = (patch: Partial<InternalListFilters>) => onChange({ ...value, ...patch })

  /**
   * What can be filtered and sorted. NOT a grid's columns — the list is cards — but the
   * same description the shared toolbar reads, which is why it can drive a card list.
   *
   * Ordering is load-bearing twice over, because `ActionLine` hands this one array to both
   * controls: the drawer renders its filterable entries in this order and the Sort menu
   * lists all of them in it. Dates first so Sort opens on the orderings anyone actually
   * wants; then Type, Status and Company in the order the card shows them.
   */
  const columns = useMemo(
    () => [
      {
        // VRG-726 — the default order (see DEFAULT_SORT), so it leads the Sort menu.
        field: 'lastActivityAt',
        headerName: 'Last activity',
        type: 'dateTime',
        filterable: false,
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        type: 'dateTime',
        // Sort-only. A date-range facet was not asked for, and the drawer skips any
        // column marked unfilterable.
        filterable: false,
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        type: 'dateTime',
        filterable: false,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'statusChip',
        // `sortType` overrides the render type for sort SEMANTICS: 'statusChip' has no
        // ordering, so without this the control falls through to its unlabelled default.
        sortType: 'string',
        options: SUPPORT_TYPE_VALUES.map(value => ({
          value,
          label: SUPPORT_REQUEST_TYPES[value].label,
          color: SUPPORT_REQUEST_TYPES[value].color,
        })),
        // The facet draws itself with the SAME component the card uses (§36) rather than
        // a lookalike built from the options' colours.
        renderOption: (option: { value: string }, isSelected: boolean) => (
          <SupportTypeBadge type={option.value} size="medium" selected={isSelected} />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'statusChip',
        sortType: 'string',
        // Vera's vocabulary, not the brand's: "Waiting on customer", not "Waiting on you".
        // Same map the card's chip reads, so a filter pill and the pill it selects are the
        // same token.
        options: statusFilterOptions(SUPPORT_REQUEST_STATUSES_INTERNAL),
      },
      {
        /*
          VRG-726 — where the request came from: an automatic report nobody spoke to, one a
          person also told us about, or a person's own report. Same chip shape as Status, so
          the drawer's pill is the card's pill (§36).
        */
        field: 'reportSource',
        headerName: 'Source',
        type: 'statusChip',
        sortType: 'string',
        options: statusFilterOptions(SUPPORT_REPORT_SOURCES),
      },
      {
        // VRG-726 — sort-only: "the automatic report that keeps happening" first.
        field: 'occurrenceCount',
        headerName: 'Occurrences',
        type: 'number',
        filterable: false,
      },
      {
        field: 'companyName',
        headerName: 'Company',
        // A row-derived facet rather than a fixed catalog: the options are the companies
        // that have actually raised something, which is a far shorter and more useful list
        // than every company on the platform. Sorted and searchable by the shared control
        // once it passes a dozen entries.
        type: 'multiSelect',
        sortType: 'string',
        options: Array.from(new Set(requests.map(companyLabel))).sort((a, b) =>
          a.localeCompare(b),
        ),
        /*
          `renderCell` so the chip carries the same `CompanyAvatar` the card does — a company
          you pick in the drawer looks like the company on the rows it selects (§36), instead
          of being a bare word that resembles one.

          It used to carry a SECOND job, and no longer does: the drawer's default renderer ran
          every bare string option through `capitalize`, which upper-cases the first letter and
          LOWER-CASES everything after it, so it rendered "Louis Vuitton" as "Louis vuitton" —
          the drawer misspelling a customer's name back at a Vera staffer. That was worked
          around here rather than fixed, on the assumption that a central fix would regress the
          enum-ish facets that depend on the flattening. It doesn't have to: `formatEnumLabel`
          transforms a value only when it is SHAPED like an enum key (all-lowercase, `_`
          separated), so `best_for` still reads "Best for" while a proper noun is passed through
          untouched. The avatar is the only reason this hook is still here.
        */
        renderCell: ({ value }: { value: string }) => (
          <Stack direction="row" spacing={2} alignItems="center">
            <CompanyAvatar name={value} size={18} radius="4px" />
            <Typography variant="body4" sx={{ whiteSpace: 'nowrap' }}>
              {value}
            </Typography>
          </Stack>
        ),
      },
    ],
    [requests],
  )

  /**
   * Translate the shared `Sort` control's callback into a sort model.
   *
   * It speaks four shapes depending on what the user did — a whole replacement array
   * (drag-reorder), a clear-all, a column added, a column's direction changed — because it
   * was written against the DataGrid API and keeps that vocabulary on the `onChange`
   * fallback path. Handled the same way the brand list and the tag Shop handle it.
   */
  const handleSortChange = (
    column: string | InternalSortItem[],
    direction?: 'asc' | 'desc' | 'clear',
    override?: boolean,
  ) => {
    if (Array.isArray(column)) return set({ sort: column })
    if (column === 'all' && direction === 'clear') return set({ sort: [] })
    if (typeof column !== 'string' || !direction || direction === 'clear') return

    if (override) return set({ sort: [{ field: column, sort: direction }] })

    const existing = value.sort.findIndex(s => s.field === column)
    if (existing === -1) {
      return set({ sort: [...value.sort, { field: column, sort: direction }] })
    }
    const next = [...value.sort]
    next[existing] = { field: column, sort: direction }
    set({ sort: next })
  }

  return (
    <ActionLine
      // No row selection on a card list, so the control that toggles it would do nothing.
      showBulkActions={false}
      columns={columns}
      sort={value.sort}
      showSort
      showFilters
      search={value.query}
      onSearchChange={next => set({ query: next })}
      // Two-way, not just out — the drawer has to be able to SEE a reset, which is what
      // "Clear filters" does to this state. See the sync effect in `Filters`.
      filters={value.filters}
      onFiltersChange={next => set({ filters: next as InternalFilterItem[] })}
      onSortChange={handleSortChange}
    />
  )
}
